import React, { useId } from 'react';
import {
  Save,
  Edit3,
  Plus,
  Calculator,
  Trash2,
  AlertTriangle,
  PartyPopper,
  TriangleAlert,
  CheckCircle2,
  Loader2,
  Clock,
} from 'lucide-react';

import EditableInput from './common/EditableInput';
import Card from './common/Card';
import SafeChart from './common/SafeChart';
import GlowArea from './charts/GlowArea';
import GlowBars from './charts/GlowBars';
import ChartDefs from './charts/ChartDefs';
import useChartTheme from './charts/useChartTheme';
import { makeChartIds, lighten } from './charts/chartTokens';
import { hasUnsavedDebtChanges, stableStringify } from './debt/debtWriteGuard';

// Tab signature accent (§ chart palette). CHARTS, progress bars, and stat badges on
// this tab glow in CORAL; green/red still carry positive/negative directional deltas.
const CORAL_TEXT = { color: 'var(--chart-coral)' };

/**
 * DebtTab – Enhanced with interest tracking and payoff calculations
 *
 * Props
 *  ─ debts:                Debt[] (now includes apr, monthlyPayment, totalInterestPaid)
 *  ─ editMode:             boolean
 *  ─ onToggleEdit():       flip edit state
 *  ─ updateDebt(i,k,val):  parent updater
 *  ─ addDebt():            append a blank debt record
 *  ─ removeDebt(i):        delete debt record
 *  ─ chartsReady:          boolean
 */
const DebtTab = ({
  debts,
  editMode,
  onToggleEdit,
  updateDebt,
  addDebt,
  removeDebt,
  chartsReady = true,
  /* ─── ARC-60: committed (server-confirmed) state, explicit save, fail-closed
     reads. All optional — without them the tab behaves exactly as it did
     before, but the shell (FinancialDashboard) supplies them, and only then can
     this tab tell "saved" apart from "typed". */
  committedDebts,
  onSaveDebts,       // async () => { success, error } — performs a real save
  hasUnsavedChanges, // optional override of the local dirty check
  loadError,         // non-empty when the debt read failed: tab goes read-only
}) => {
  // State for extra payment scenario
  const [showComparison, setShowComparison] = React.useState(false);
  // Store extra payment per debt: { debtIndex: { amount: 0, mode: 'dollar' } }
  const [debtExtraPayments, setDebtExtraPayments] = React.useState({});

  /* ─── ARC-60: fail closed ──────────────────────────────────────────────
     A failed read tells us nothing about what the server holds. Every path
     that could write is shut off — not just Save, but editing, adding debts
     and deleting them, so there is nothing to lose by refusing to save. */
  const readOnly = Boolean(loadError);
  const canEdit = editMode && !readOnly;

  /* ─── ARC-60: committed state ──────────────────────────────────────────
     The debt cards below are the edit buffer. Everything DERIVED from them —
     the projection chart, the payoff bars and the summary card — reads the last
     SERVER-CONFIRMED state, so a member is never shown a figure that was not
     persisted. Falls back to the buffer when the shell passes no committed
     snapshot (pre-ARC-60 behaviour); after a failed read there is neither. */
  const hasCommitted = Array.isArray(committedDebts);
  const savedDebts = hasCommitted ? committedDebts : debts;

  const dirty =
    typeof hasUnsavedChanges === 'boolean'
      ? hasUnsavedChanges
      : hasCommitted && hasUnsavedDebtChanges({ debts }, { debts: committedDebts });

  /* ─── ARC-60: explicit save result ─────────────────────────────────────
     { kind: "saving" | "success" | "error", text, signature }. Leaving edit
     mode is not evidence of persistence; every save resolves to one of these. */
  const [saveStatus, setSaveStatus] = React.useState(null);
  const saving = saveStatus?.kind === 'saving';
  // A "saved" notice describes the buffer as it was when it was written, so it
  // stands only while the buffer is unchanged since that write.
  const bufferSignature = stableStringify({ debts });
  const savedNoticeStillTrue = !dirty || saveStatus?.signature === bufferSignature;
  const visibleStatus =
    saveStatus?.kind === 'success' && !savedNoticeStillTrue ? null : saveStatus;

  const handleEditSaveClick = async () => {
    if (readOnly || saving) return;

    if (!editMode) {
      setSaveStatus(null);
      if (onToggleEdit) onToggleEdit();
      return;
    }

    // No save handler wired — keep the old toggle, and claim nothing was saved.
    if (typeof onSaveDebts !== 'function') {
      if (onToggleEdit) onToggleEdit();
      return;
    }

    setSaveStatus({ kind: 'saving', text: 'Saving your changes…' });

    let result;
    try {
      result = await onSaveDebts();
    } catch (e) {
      result = { success: false, error: e?.message };
    }

    if (result && result.success) {
      setSaveStatus({
        kind: 'success',
        text: 'Changes saved. The figures below come from your saved data.',
        signature: stableStringify({ debts }),
      });
      if (onToggleEdit) onToggleEdit();
      return;
    }

    /* Failed: stay in edit mode so every entered value stays on screen and
       editable. Some upstream errors already promise the entries survived;
       adding our own to those would print that promise twice. */
    const reassures = (msg) => /still here|still on screen/i.test(msg);
    setSaveStatus({
      kind: 'error',
      text: result?.error
        ? reassures(result.error)
          ? `Not saved: ${result.error}`
          : `Not saved: ${result.error} Your entries are still here — try Save again.`
        : 'Not saved. Your entries are still here — check your connection and try Save again.',
    });
  };

  // Helper to get extra payment info for a debt
  const getExtraPaymentForDebt = (debtIndex) => {
    return debtExtraPayments[debtIndex] || { amount: 0, mode: 'dollar' };
  };

  // Helper to update extra payment for a debt
  const updateDebtExtraPayment = (debtIndex, field, value) => {
    setDebtExtraPayments(prev => ({
      ...prev,
      [debtIndex]: {
        ...(prev[debtIndex] || { amount: 0, mode: 'dollar' }),
        [field]: value
      }
    }));
  };

  // Progress bars reuse the chart glow system: the same arcGlow filter + coral/pos
  // tokens as this tab's charts (via ChartDefs/useChartTheme/chartTokens), so the
  // bars match the charts and dial the glow back in light mode automatically.
  const glow = useChartTheme();
  const glowIds = makeChartIds(useId());
  const debtFillStyle = (paidOff) => {
    const tone = paidOff >= 100 ? 'pos' : 'coral'; // keep the >=100% green switch
    return {
      background: `linear-gradient(180deg, ${lighten(glow.tone[tone], 0.45)}, ${glow.tone[tone]})`,
      filter: `url(#${glowIds.glow})`,
    };
  };

  // Calculate monthly interest and payoff projections for each debt
  const calculateDebtMetrics = (debt) => {
    const monthlyRate = (debt.apr || 0) / 100 / 12;
    const monthlyInterest = debt.current * monthlyRate;
    const minimumPayment = debt.monthlyPayment || 0;
    
    let monthsToPayoff = 0;
    let totalInterestRemaining = 0;
    
    if (minimumPayment > monthlyInterest && debt.current > 0) {
      // Calculate payoff time using amortization formula
      const principal = minimumPayment - monthlyInterest;
      if (principal > 0) {
        monthsToPayoff = Math.ceil(Math.log(1 + (debt.current * monthlyRate) / principal) / Math.log(1 + monthlyRate));
        totalInterestRemaining = (minimumPayment * monthsToPayoff) - debt.current;
      }
    }
    
    return {
      monthlyInterest: monthlyInterest,
      monthsToPayoff: monthsToPayoff,
      totalInterestRemaining: Math.max(0, totalInterestRemaining),
      principalPayment: Math.max(0, minimumPayment - monthlyInterest)
    };
  };

  // Calculate total debt projection over time
  // ARC-60: projected from committed (saved) debts, like every derived widget
  // below — a payoff date is a claim about persisted data, not about typing.
  const calculateDebtProjection = (useExtraPayments = false) => {
    const activeDebts = savedDebts.filter(d => d.current > 0 && d.original > 0);
    if (activeDebts.length === 0) return [];

    // Calculate max months needed
    const maxMonthsBase = Math.max(
      ...activeDebts.map(debt => {
        const metrics = calculateDebtMetrics(debt);
        return metrics.monthsToPayoff || 0;
      }),
      12
    );

    const projectionData = [];
    
    for (let month = 0; month <= Math.min(maxMonthsBase + 60, 120); month++) {
      let totalRemaining = 0;
      let totalInterestPaid = 0;
      
      activeDebts.forEach((debt, debtIdx) => {
        const monthlyRate = (debt.apr || 0) / 100 / 12;
        const basePayment = debt.monthlyPayment || 0;
        
        // Calculate extra payment for this specific debt
        let extraPayment = 0;
        if (useExtraPayments) {
          const extraInfo = getExtraPaymentForDebt(debtIdx);
          if (extraInfo.mode === 'dollar') {
            extraPayment = extraInfo.amount || 0;
          } else {
            // Percentage mode - percentage of current monthly payment
            extraPayment = basePayment * ((extraInfo.amount || 0) / 100);
          }
        }
        
        const totalPayment = basePayment + extraPayment;
        
        let balance = debt.current;
        let interestAccrued = 0;
        
        for (let m = 0; m < month; m++) {
          if (balance <= 0) break;
          
          const interest = balance * monthlyRate;
          interestAccrued += interest;
          const principal = Math.min(totalPayment - interest, balance);
          
          if (principal <= 0) {
            balance += interest;
          } else {
            balance = balance + interest - totalPayment;
            balance = Math.max(0, balance);
          }
        }
        
        totalRemaining += Math.max(0, balance);
        totalInterestPaid += interestAccrued;
      });

      projectionData.push({
        month: month,
        totalDebt: Math.round(totalRemaining),
        totalInterest: Math.round(totalInterestPaid),
        monthLabel: month === 0 ? 'Now' : `${month}mo`
      });
      
      // Stop if all debt is paid off
      if (totalRemaining === 0 && month > 0) break;
    }

    return projectionData;
  };

  // Calculate both projections
  const currentProjection = calculateDebtProjection(false);
  const acceleratedProjection = showComparison ? calculateDebtProjection(true) : [];
  
  // Merge projections for chart display
  const maxLength = Math.max(currentProjection.length, acceleratedProjection.length);
  const projectionData = Array.from({ length: maxLength }, (_, idx) => {
    const current = currentProjection[idx];
    const accelerated = acceleratedProjection[idx];
    return {
      month: idx,
      monthLabel: idx === 0 ? 'Now' : `${idx}mo`,
      totalDebt: current ? current.totalDebt : 0,
      totalInterest: current ? current.totalInterest : 0,
      acceleratedDebt: accelerated ? accelerated.totalDebt : null,
      acceleratedInterest: accelerated ? accelerated.totalInterest : null
    };
  });

  // Calculate savings metrics
  const currentPayoffMonth = currentProjection.findIndex(d => d.totalDebt === 0);
  const acceleratedPayoffMonth = showComparison ? acceleratedProjection.findIndex(d => d.totalDebt === 0) : -1;
  const monthsSaved = currentPayoffMonth > 0 && acceleratedPayoffMonth > 0 
    ? currentPayoffMonth - acceleratedPayoffMonth 
    : 0;
  
  const currentTotalInterest = currentPayoffMonth > 0 
    ? currentProjection[currentPayoffMonth]?.totalInterest || 0 
    : 0;
  const acceleratedTotalInterest = acceleratedPayoffMonth > 0 
    ? acceleratedProjection[acceleratedPayoffMonth]?.totalInterest || 0 
    : 0;
  const interestSaved = currentTotalInterest - acceleratedTotalInterest;
  
  // Calculate total extra payment per month
  const totalExtraPayment = savedDebts.reduce((sum, debt, idx) => {
    const extraInfo = getExtraPaymentForDebt(idx);
    const basePayment = debt.monthlyPayment || 0;
    let extra = 0;
    if (extraInfo.mode === 'dollar') {
      extra = extraInfo.amount || 0;
    } else {
      extra = basePayment * ((extraInfo.amount || 0) / 100);
    }
    return sum + extra;
  }, 0);

  // Prepare chart data - only include debts with original amounts > 0
  const chartData = savedDebts
    .filter(debt => debt.original > 0)
    .map(debt => {
      const metrics = calculateDebtMetrics(debt);
      return {
        name: debt.name.length > 15 ? debt.name.substring(0, 15) + '...' : debt.name,
        fullName: debt.name,
        paidOff: debt.paidOff,
        remaining: debt.current,
        original: debt.original,
        monthlyInterest: metrics.monthlyInterest,
        monthsToPayoff: metrics.monthsToPayoff
      };
    });

  // Calculate totals for summary
  const summaryData = savedDebts
    .filter(d => d.original > 0)
    .reduce((acc, debt) => {
      const metrics = calculateDebtMetrics(debt);
      return {
        totalOriginal: acc.totalOriginal + debt.original,
        totalRemaining: acc.totalRemaining + debt.current,
        totalPaidOff: acc.totalPaidOff + (debt.original - debt.current),
        totalMonthlyInterest: acc.totalMonthlyInterest + metrics.monthlyInterest,
        totalMonthlyPayments: acc.totalMonthlyPayments + (debt.monthlyPayment || 0),
        totalInterestPaid: acc.totalInterestPaid + (debt.totalInterestPaid || 0),
        totalInterestRemaining: acc.totalInterestRemaining + metrics.totalInterestRemaining
      };
    }, {
      totalOriginal: 0,
      totalRemaining: 0,
      totalPaidOff: 0,
      totalMonthlyInterest: 0,
      totalMonthlyPayments: 0,
      totalInterestPaid: 0,
      totalInterestRemaining: 0
    });

  // Line-chart series — coral (tab accent) for the current plan; green/pos for the
  // accelerated plan since a faster payoff is a positive delta.
  const debtSeries = [{ key: 'totalDebt', name: 'Current Plan', tone: 'coral' }];
  if (showComparison) {
    debtSeries.push({ key: 'acceleratedDebt', name: 'With Extra Payment', tone: 'pos' });
  }

  return (
    <div className="space-y-6">
      {/* Shared glow <defs> for the progress bars below (SVG ids are document-global). */}
      <ChartDefs ids={glowIds} colors={glow} />

      {/* ─── ARC-60: failed read ───────────────────────────────────────────
          The tab is read-only and saving is disabled entirely. Writing an empty
          or placeholder debt list on top of data we failed to read is the
          overwrite this issue exists to prevent. */}
      {readOnly && (
        <div
          role="alert"
          className="flex gap-3 p-4 rounded-arc-card shadow-arc-sm ring-1 bg-arc-neg-soft ring-arc-neg-soft text-arc-neg"
        >
          <TriangleAlert size={18} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
          <div className="text-[14.5px] leading-[1.55]">{loadError}</div>
        </div>
      )}

      {/* ─── ARC-60: explicit save outcome ─────────────────────────────────
          Leaving edit mode is not confirmation. Every save resolves here to a
          visible saving / saved / not-saved state. On failure the tab stays in
          edit mode and the entered values stay in the buffer. */}
      {visibleStatus && (
        <div
          role={visibleStatus.kind === 'error' ? 'alert' : 'status'}
          aria-live={visibleStatus.kind === 'error' ? 'assertive' : 'polite'}
          className={`flex gap-3 p-4 rounded-arc-card shadow-arc-sm ring-1 ${
            visibleStatus.kind === 'error'
              ? 'bg-arc-neg-soft ring-arc-neg-soft text-arc-neg'
              : visibleStatus.kind === 'success'
              ? 'bg-arc-pos-soft ring-arc-pos-soft text-arc-pos'
              : 'bg-arc-paper-2 ring-arc-line text-arc-ink-2'
          }`}
        >
          {visibleStatus.kind === 'error' ? (
            <TriangleAlert size={18} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
          ) : visibleStatus.kind === 'success' ? (
            <CheckCircle2 size={18} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
          ) : (
            <Loader2 size={18} className="flex-shrink-0 mt-0.5 animate-spin" aria-hidden="true" />
          )}
          <div className="text-[14.5px] leading-[1.55]">{visibleStatus.text}</div>
        </div>
      )}

      {/* ARC-60: pending-edits marker. Says plainly that the charts and summary
          are the saved figures, so nothing here can be mistaken for a save. */}
      {dirty && savedDebts.length > 0 && (
        <div
          role="status"
          className="flex items-center gap-2 px-3 py-2 rounded-arc-ctl ring-1 ring-arc-line bg-arc-paper-2 text-arc-ink-2 text-[12.5px]"
        >
          <Clock size={14} className="flex-shrink-0 text-arc-ink-3" aria-hidden="true" />
          <span>
            Unsaved edits below. The charts and summary show your last saved
            debts until you save.
          </span>
        </div>
      )}

      {/* Line Chart - Total Debt Projection */}
      {projectionData.length > 0 && (
        <Card hover={false}>
          <div className="flex flex-wrap justify-between items-center mb-4">
            <h3 className="text-h2 text-arc-ink">Total Debt Projection</h3>

            {/* Comparison Toggle */}
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showComparison}
                  onChange={(e) => setShowComparison(e.target.checked)}
                  className="w-4 h-4 rounded accent-arc-accent focus:ring-2 focus:ring-arc-accent"
                />
                <span className="text-sm font-medium text-arc-ink-2">Compare with Extra Payment</span>
              </label>
            </div>
          </div>

          {/* Extra Payment Input */}
          {showComparison && (
            <div className="mb-4 p-4 bg-arc-paper-2 rounded-arc-ctl ring-1 ring-arc-line">
              <h4 className="text-sm font-semibold text-arc-ink mb-3">Configure Extra Payments per Debt</h4>

              {/* Per-debt extra payment inputs */}
              <div className="space-y-3 mb-4">
                {savedDebts.filter(d => d.current > 0 && d.original > 0).map((debt, idx) => {
                  const extraInfo = getExtraPaymentForDebt(idx);
                  const basePayment = debt.monthlyPayment || 0;
                  const extraAmount = extraInfo.mode === 'dollar'
                    ? (extraInfo.amount || 0)
                    : basePayment * ((extraInfo.amount || 0) / 100);
                  const newPayment = basePayment + extraAmount;

                  return (
                    <div key={idx} className="bg-arc-card p-3 rounded-arc-ctl ring-1 ring-arc-line">
                      <div className="flex flex-wrap items-center gap-3">
                        {/* Debt Name */}
                        <div className="flex-1 min-w-[150px]">
                          <p className="font-medium text-sm text-arc-ink">{debt.name}</p>
                          <p className="text-xs text-arc-ink-4 tnum">
                            Current: ${basePayment.toLocaleString()}/mo
                          </p>
                        </div>

                        {/* Mode Toggle */}
                        <div className="flex gap-[3px] bg-arc-paper-2 p-1 rounded-arc-ctl ring-1 ring-arc-line">
                          <button
                            onClick={() => updateDebtExtraPayment(idx, 'mode', 'dollar')}
                            className={`px-3 py-1 text-xs rounded-[9px] font-medium transition ${
                              extraInfo.mode === 'dollar'
                                ? 'bg-arc-card text-arc-ink shadow-arc-sm'
                                : 'bg-transparent text-arc-ink-3 hover:text-arc-ink'
                            }`}
                          >
                            $
                          </button>
                          <button
                            onClick={() => updateDebtExtraPayment(idx, 'mode', 'percentage')}
                            className={`px-3 py-1 text-xs rounded-[9px] font-medium transition ${
                              extraInfo.mode === 'percentage'
                                ? 'bg-arc-card text-arc-ink shadow-arc-sm'
                                : 'bg-transparent text-arc-ink-3 hover:text-arc-ink'
                            }`}
                          >
                            %
                          </button>
                        </div>

                        {/* Amount Input */}
                        <div className="flex items-center gap-2 min-w-[120px]">
                          <input
                            type="number"
                            min="0"
                            step={extraInfo.mode === 'dollar' ? '10' : '1'}
                            value={extraInfo.amount || 0}
                            onChange={(e) => updateDebtExtraPayment(idx, 'amount', Number(e.target.value))}
                            className="w-full px-2 py-1 text-sm tnum bg-arc-paper-2 text-arc-ink border border-arc-line rounded-arc-sm focus:ring-2 focus:ring-arc-accent focus:border-arc-accent"
                            placeholder="0"
                          />
                          <span className="text-xs text-arc-ink-3">
                            {extraInfo.mode === 'dollar' ? '$' : '%'}
                          </span>
                        </div>

                        {/* New Payment Display */}
                        <div className="text-right min-w-[100px]">
                          <p className="text-xs text-arc-ink-3">New Payment</p>
                          <p className="font-semibold text-sm tnum text-arc-pos">
                            ${newPayment.toFixed(2)}/mo
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Total Summary */}
              <div className="flex flex-wrap gap-4 pt-3 border-t border-arc-line">
                <div className="flex-1 bg-arc-card rounded-arc-ctl p-3 ring-1 ring-arc-line">
                  <h5 className="text-sm font-semibold text-arc-ink mb-2">Payment Summary</h5>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <p className="text-arc-ink-3">Total Extra/Month</p>
                      <p className="font-bold text-base tnum" style={CORAL_TEXT}>
                        ${totalExtraPayment.toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-arc-ink-3">New Total Payment</p>
                      <p className="font-bold text-base tnum" style={CORAL_TEXT}>
                        ${(summaryData.totalMonthlyPayments + totalExtraPayment).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Savings Summary */}
                {monthsSaved > 0 && (
                  <div className="flex-1 bg-arc-card rounded-arc-ctl p-3 ring-1 ring-arc-line">
                    <h5 className="text-sm font-semibold text-arc-ink mb-2">Projected Savings</h5>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <p className="text-arc-ink-3">Time Saved</p>
                        <p className="font-bold text-arc-pos text-base tnum">
                          {monthsSaved} months
                        </p>
                      </div>
                      <div>
                        <p className="text-arc-ink-3">Interest Saved</p>
                        <p className="font-bold text-arc-pos text-base tnum">
                          ${interestSaved.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <SafeChart chartsReady={chartsReady}>
            <GlowArea
              data={projectionData}
              xKey="monthLabel"
              series={debtSeries}
              yTickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              valueFormatter={(v) => `$${Number(v).toLocaleString()}`}
            />
          </SafeChart>
        </Card>
      )}

      {/* Bar Chart Section */}
      {chartData.length > 0 && (
        <Card hover={false}>
          <h3 className="text-h2 text-arc-ink mb-4">Debt Payoff Progress</h3>
          <SafeChart chartsReady={chartsReady}>
            <GlowBars
              data={chartData}
              dataKey="paidOff"
              xKey="name"
              xAxisProps={{ angle: -45, textAnchor: 'end', height: 80 }}
              yTickFormatter={(value) => `${value.toFixed(0)}%`}
              valueFormatter={(v) => `${Number(v).toFixed(1)}%`}
              cellTone={(entry) => (entry.paidOff >= 100 ? 'pos' : 'coral')}
            />
          </SafeChart>
        </Card>
      )}

      {savedDebts.filter(d => d.original > 0).length > 0 && (
          <Card hover={false} className="bg-arc-paper-2">
            <h4 className="text-h2 text-arc-ink mb-3">Debt Summary</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              {/* Original totals */}
              <div>
                <p className="text-arc-ink-3">Total Original Debt</p>
                <p className="font-semibold text-lg tnum text-arc-ink">
                  ${summaryData.totalOriginal.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-arc-ink-3">Total Remaining</p>
                <p className="font-semibold text-lg tnum text-arc-neg">
                  ${summaryData.totalRemaining.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-arc-ink-3">Total Paid Off</p>
                <p className="font-semibold text-lg tnum text-arc-pos">
                  ${summaryData.totalPaidOff.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-arc-ink-3">Overall Progress</p>
                <p className="font-semibold text-lg tnum" style={CORAL_TEXT}>
                  {summaryData.totalOriginal > 0 ?
                    ((summaryData.totalPaidOff / summaryData.totalOriginal) * 100).toFixed(1) : 0}%
                </p>
              </div>
            </div>

            {/* Interest summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm mt-4 pt-4 border-t border-arc-line">
              <div>
                <p className="text-arc-ink-3">Monthly Interest Cost</p>
                <p className="font-semibold text-lg tnum text-arc-warn">
                  ${summaryData.totalMonthlyInterest.toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-arc-ink-3">Monthly Payments</p>
                <p className="font-semibold text-lg tnum" style={CORAL_TEXT}>
                  ${summaryData.totalMonthlyPayments.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-arc-ink-3">Total Interest Paid</p>
                <p className="font-semibold text-lg tnum text-arc-warn">
                  ${summaryData.totalInterestPaid.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-arc-ink-3">Est. Remaining Interest</p>
                <p className="font-semibold text-lg tnum text-arc-neg">
                  ${summaryData.totalInterestRemaining.toFixed(2)}
                </p>
              </div>
            </div>
          </Card>
        )}

      {/* Debt Management Section */}
      <Card hover={false}>
        {/* Header */}
        <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
          <h3 className="text-h2 text-arc-ink">Debt Tracking</h3>

          <div className="flex gap-2">
            <button
              onClick={addDebt}
              disabled={readOnly}
              className="flex items-center gap-2 px-4 py-2.5 rounded-arc-ctl bg-arc-accent text-white font-semibold shadow-arc-sm transition hover:brightness-105 active:brightness-95 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Plus size={16} /> Add&nbsp;Debt
            </button>

            <button
              onClick={handleEditSaveClick}
              disabled={readOnly || saving}
              aria-busy={saving}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-arc-ctl text-white font-semibold shadow-arc-sm transition hover:brightness-105 active:brightness-95 disabled:opacity-60 disabled:cursor-not-allowed ${
                editMode ? 'bg-arc-pos' : 'bg-arc-accent'
              }`}
            >
              {saving ? (
                <Loader2 size={16} className="animate-spin" />
              ) : editMode ? (
                <Save size={16} />
              ) : (
                <Edit3 size={16} />
              )}{' '}
              {saving ? 'Saving…' : editMode ? 'Save' : 'Edit'}
            </button>
          </div>
        </div>

        {/* Debt Cards */}
        <div className="space-y-4">
          {/* ARC-60: the cards ARE the edit surface, so they render the buffer
              rather than committed state — the whole point is to show what has
              been typed. Everything derived from them above reads committed. */}
          {debts
            .filter((d) => d.original > 0 || canEdit)
            .map((debt, idx) => {
              const metrics = calculateDebtMetrics(debt);
              return (
                <div key={idx} className="rounded-arc-card ring-1 ring-arc-line p-4">
                  {/* Title */}
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-lg text-arc-ink">
                      {canEdit ? (
                        <EditableInput
                          type="text"
                          value={debt.name}
                          onChange={(v) => updateDebt(idx, 'name', v)}
                          className="font-medium text-lg"
                        />
                      ) : (
                        debt.name
                      )}
                    </h4>
                    {canEdit && (
                      <button
                        onClick={() => removeDebt(idx)}
                        className="text-arc-neg hover:opacity-80 p-2"
                        title="Delete debt"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>

                  {/* Progress bar — glowing coral fill (green once fully paid). */}
                  <div className="w-full bg-arc-paper-2 rounded-full h-3 mb-3">
                    <div
                      className="h-3 rounded-full transition-all duration-300"
                      style={{
                        width: `${Math.min(debt.paidOff, 100)}%`,
                        ...debtFillStyle(debt.paidOff),
                      }}
                    />
                  </div>

                  {/* Main stats grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                    {/* Original Amount */}
                    <div>
                      <p className="text-arc-ink-3 mb-1">Original Amount</p>
                      <p className="font-semibold tnum text-arc-ink">
                        {canEdit ? (
                          <EditableInput
                            value={debt.original}
                            onChange={(v) => updateDebt(idx, 'original', v)}
                            className="w-full text-sm"
                          />
                        ) : (
                          `$${debt.original.toLocaleString()}`
                        )}
                      </p>
                    </div>

                    {/* Remaining Balance */}
                    <div>
                      <p className="text-arc-ink-3 mb-1">Remaining Balance</p>
                      <p className="font-semibold tnum text-arc-ink">
                        {canEdit ? (
                          <EditableInput
                            value={debt.current}
                            onChange={(v) => updateDebt(idx, 'current', v)}
                            className="w-full text-sm"
                          />
                        ) : (
                          `$${debt.current.toLocaleString()}`
                        )}
                      </p>
                    </div>

                    {/* APR */}
                    <div>
                      <p className="text-arc-ink-3 mb-1">APR (%)</p>
                      <p className="font-semibold tnum text-arc-ink">
                        {canEdit ? (
                          <EditableInput
                            value={debt.apr || 0}
                            onChange={(v) => updateDebt(idx, 'apr', v)}
                            className="w-full text-sm"
                          />
                        ) : (
                          `${(debt.apr || 0).toFixed(2)}%`
                        )}
                      </p>
                    </div>

                    {/* Monthly Payment */}
                    <div>
                      <p className="text-arc-ink-3 mb-1">Monthly Payment</p>
                      <p className="font-semibold tnum text-arc-ink">
                        {canEdit ? (
                          <EditableInput
                            value={debt.monthlyPayment || 0}
                            onChange={(v) => updateDebt(idx, 'monthlyPayment', v)}
                            className="w-full text-sm"
                          />
                        ) : (
                          `$${(debt.monthlyPayment || 0).toLocaleString()}`
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Interest calculations */}
                  {debt.current > 0 && debt.apr > 0 && (
                    <div className="bg-arc-warn-soft ring-1 ring-arc-line rounded-arc-ctl p-3 mb-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Calculator size={16} className="text-arc-warn" />
                        <h5 className="font-medium text-arc-ink">Interest Analysis</h5>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                        <div>
                          <p className="text-arc-ink-3">Monthly Interest</p>
                          <p className="font-semibold tnum text-arc-warn">
                            ${metrics.monthlyInterest.toFixed(2)}
                          </p>
                        </div>
                        <div>
                          <p className="text-arc-ink-3">Principal Payment</p>
                          <p className="font-semibold tnum text-arc-warn">
                            ${metrics.principalPayment.toFixed(2)}
                          </p>
                        </div>
                        <div>
                          <p className="text-arc-ink-3">Months to Payoff</p>
                          <p className="font-semibold tnum text-arc-warn">
                            {metrics.monthsToPayoff > 0 ? `${metrics.monthsToPayoff} months` : 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-arc-ink-3">Remaining Interest</p>
                          <p className="font-semibold tnum text-arc-warn">
                            ${metrics.totalInterestRemaining.toFixed(2)}
                          </p>
                        </div>
                      </div>
                      {debt.monthlyPayment <= metrics.monthlyInterest && debt.current > 0 && (
                        <div className="mt-2 p-2 bg-arc-neg-soft ring-1 ring-arc-line rounded-arc-sm text-xs">
                          <p className="text-arc-neg font-medium flex items-center gap-1.5">
                            <AlertTriangle size={14} className="shrink-0" />
                            Warning: Monthly payment is less than interest. Debt will grow!
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Progress display */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-arc-ink-3 mb-1">Progress</p>
                      <p
                        className="font-semibold tnum"
                        style={debt.paidOff >= 100 ? undefined : CORAL_TEXT}
                      >
                        <span className={debt.paidOff >= 100 ? 'text-arc-pos' : undefined}>
                          {debt.paidOff.toFixed(1)}% paid off
                        </span>
                      </p>
                    </div>
                    <div>
                      <p className="text-arc-ink-3 mb-1">Total Interest Paid</p>
                      <p className="font-semibold tnum text-arc-ink">
                        {canEdit ? (
                          <EditableInput
                            value={debt.totalInterestPaid || 0}
                            onChange={(v) => updateDebt(idx, 'totalInterestPaid', v)}
                            className="w-full text-sm"
                          />
                        ) : (
                          `$${(debt.totalInterestPaid || 0).toLocaleString()}`
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Fully paid indicator */}
                  {debt.paidOff >= 100 && (
                    <div className="mt-3 p-2 bg-arc-pos-soft ring-1 ring-arc-line rounded-arc-ctl">
                      <p className="text-arc-pos text-sm font-medium text-center flex items-center justify-center gap-1.5">
                        <PartyPopper size={16} className="shrink-0" />
                        Congratulations! This debt is fully paid off!
                      </p>
                    </div>
                  )}
                </div>
              );
            })}

          {/* ARC-60: debts no longer start from a placeholder scaffold, so an
              account that has entered nothing yet lands here. */}
          {debts.filter((d) => d.original > 0 || canEdit).length === 0 && (
            <p className="px-3 py-8 text-center text-sm text-arc-ink-3">
              {readOnly
                ? 'Your debt data could not be loaded.'
                : 'No debts tracked yet. Click "Add Debt" to start tracking one.'}
            </p>
          )}
        </div>

      </Card>
    </div>
  );
};

export default DebtTab;